import * as React from "react";

import { cn } from "@/lib/utils";

import { Avatar, AvatarFallback } from "./avatar";
import { KindIcon, KINDS, type Kind } from "./kind-icon";

type RailSectionProps = Omit<React.ComponentProps<"section">, "title"> & {
  kind?: Kind;
  /** Defaults to the kind's label. */
  title?: React.ReactNode;
  count?: number;
};

/**
 * A For Review rail section: a 28px header (13px type icon in its hue, 12px
 * `fg-3` label, `fg-4` count) labeled once, so rows never repeat the type.
 */
function RailSection({
  kind,
  title,
  count,
  className,
  children,
  ...props
}: RailSectionProps) {
  return (
    <section
      data-slot="rail-section"
      className={cn("flex flex-col", className)}
      {...props}
    >
      <div className="flex h-7 items-center gap-1.5 px-2">
        {kind ? <KindIcon kind={kind} className="size-3.25" /> : null}
        <span className="truncate text-sm text-fg-3">
          {title ?? (kind ? KINDS[kind].label : null)}
        </span>
        {typeof count === "number" ? (
          <span className="text-sm text-fg-4 tabular-nums">{count}</span>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </section>
  );
}

type InboxRowProps = Omit<React.ComponentProps<"button">, "title"> & {
  title: React.ReactNode;
  /** Client and context, 12px `fg-3`. */
  detail?: React.ReactNode;
  /** Relative time, 12px `fg-4`. */
  time?: React.ReactNode;
  /** Client name for the 20px neutral monogram. */
  avatarName?: string;
  /** Replaces the avatar (the briefing's sparkles icon). */
  media?: React.ReactNode;
  /** Replaces the time on the right (a danger `StatusBadge` when past due). */
  trailing?: React.ReactNode;
  unread?: boolean;
  selected?: boolean;
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

/** A 44px For Review row. Hover and selection only use fills. */
function InboxRow({
  title,
  detail,
  time,
  avatarName,
  media,
  trailing,
  unread = false,
  selected = false,
  className,
  ...props
}: InboxRowProps) {
  return (
    <button
      type="button"
      data-slot="inbox-row"
      aria-current={selected ? "true" : undefined}
      data-unread={unread ? "true" : undefined}
      className={cn(
        "flex h-11 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left transition-colors duration-100 outline-none hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-primary/40 aria-[current=true]:bg-fill-2",
        className
      )}
      {...props}
    >
      {media ??
        (avatarName ? (
          <Avatar size="xs">
            <AvatarFallback>{initials(avatarName)}</AvatarFallback>
          </Avatar>
        ) : null)}
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn("truncate text-base text-fg", unread && "font-medium")}
        >
          {title}
        </span>
        {detail ? (
          <span className="truncate text-sm text-fg-3">{detail}</span>
        ) : null}
      </span>
      <span className="flex shrink-0 items-center gap-1.5 self-start pt-1.5">
        {trailing ??
          (time ? (
            <span className="text-sm text-fg-4 tabular-nums">{time}</span>
          ) : null)}
        {unread ? (
          <span
            className="size-1.5 rounded-full bg-accent-seed"
            aria-label="Unread"
            data-testid="unread-dot"
          />
        ) : null}
      </span>
    </button>
  );
}

export { InboxRow, RailSection, initials };
export type { InboxRowProps, RailSectionProps };
